import { useEffect } from "react";

function selector(attr, key) {
    return `meta[${attr}='${key}']`;
}

function snapshotAndApply(attr, key, value) {
    const existing = document.querySelector(selector(attr, key));
    const hadTag = !!existing;
    const previousContent = hadTag ? existing.getAttribute("content") : null;

    if (value === null) {
        // Caller wants this tag absent (e.g. clearing a stale og:image left
        // by a previously-visited article) rather than given new content.
        if (existing) existing.remove();
    } else {
        let el = existing;
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, key);
            document.head.appendChild(el);
        }
        el.setAttribute("content", value);
    }

    return { attr, key, hadTag, previousContent };
}

function restore({ attr, key, hadTag, previousContent }) {
    const existing = document.querySelector(selector(attr, key));
    if (hadTag) {
        let el = existing;
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, key);
            document.head.appendChild(el);
        }
        el.setAttribute("content", previousContent);
    } else if (existing) {
        existing.remove();
    }
}

/**
 * Sets og:title/og:description/og:type/og:image (and, optionally,
 * twitter:card/twitter:image) for the lifetime of the mounted component
 * and restores exactly what was there before — including "the tag didn't
 * exist" — on unmount or whenever the values change. Same restore
 * discipline as useNoIndex(): a component that mutates global <head> state
 * must put it back, or a stale og:title/description/type/image (and
 * twitter:*) would otherwise leak into whatever page/state the visitor
 * lands on next (see docs/audits/SEO_AUDIT_2026-07-29.md H2 review notes,
 * 2026-07-30, and the follow-up detail-page stale-state review).
 *
 * Pass `null` for a key to force that tag absent rather than set new
 * content. Omit a key (undefined) to leave it completely untouched.
 */
export default function useOgMeta({ title, description, type, image, twitterCard, twitterImage } = {}) {
    useEffect(() => {
        const wanted = [
            ["property", "og:title", title],
            ["property", "og:description", description],
            ["property", "og:type", type],
            ["property", "og:image", image],
            ["name", "twitter:card", twitterCard],
            ["name", "twitter:image", twitterImage],
        ].filter(([, , v]) => v !== undefined);
        const snapshots = wanted.map(([attr, key, value]) => snapshotAndApply(attr, key, value));
        return () => { snapshots.forEach(restore); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, description, type, image, twitterCard, twitterImage]);
}
